import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import type { DeployedRelease, RolledPart } from './pools-types';
import { buildWord, partName } from './pools-words';

function Rolled({ part }: { part: RolledPart }): ReactNode {
  const settings = part.build !== null && part.build === part.previousBuild;
  const change = settings
    ? `${buildWord(part.build)}, new settings only`
    : part.previousBuild
      ? `${buildWord(part.previousBuild)} to ${buildWord(part.build)}`
      : buildWord(part.build);
  return (
    <li
      className={settings ? 'pl-chip pl-chip--settings' : `pl-chip pl-chip--${part.kind}`}
      title={change}
    >
      {partName(part.id)}
      {settings ? <span className="pl-chip__note"> settings</span> : null}
    </li>
  );
}

/**
 * The recent releases, newest first, as a timeline: when, the build, and what rolled (pools filled, services
 * outlined, a part that only took new settings dashed). As far back as the cluster keeps a part's history.
 */
export function ReleaseTimeline({
  releases,
  now,
}: {
  releases: DeployedRelease[];
  now: number;
}): ReactNode {
  const f = useFormat();
  if (releases.length === 0) {
    return <p className="pl-part__quiet">No release is on record yet.</p>;
  }
  return (
    <ol className="pl-releases">
      {releases.map((r, i) => (
        <li key={r.at} className={i === 0 ? 'pl-release pl-release--latest' : 'pl-release'}>
          <span className="pl-release__dot" aria-hidden="true" />
          <div className="pl-release__when">
            <strong>{f.ago(r.at, now)}</strong>
            <time dateTime={r.at}>{f.dateTime(r.at)}</time>
          </div>
          <code className="pl-release__build" title={r.build ?? undefined}>
            {r.build ? buildWord(r.build) : 'settings only'}
          </code>
          <ul className="pl-release__rolled" aria-label="What rolled">
            {r.rolled.map((p) => (
              <Rolled key={`${p.kind}-${p.id}`} part={p} />
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}
